<template>
  <div class="expert-tool-save-and-close">

    <!-- The Save Button (Overridable as <template #button="{ save }"></template>) -->

    <slot name="button" :save="initSave">
      <v-btn @click.stop="initSave" :disabled="hasReadOnlyAccess">
        {{ $ucfirst($t('save')) }}
      </v-btn>
    </slot>

    <!-- Unable to save popup -->

    <v-dialog v-model="showUnableToSaveDialog" width="600px">
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{ $ucfirst($t('expertTool.confirmation.prescriptionIncomplete')) }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn x-large text class="ml-4" @click="closeUnableToSaveDialog" color="primary">
            {{ $ucfirst($t('OK')) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- Confirm saving popup -->

    <v-dialog v-model="showConfirmationDialog" width="600px">
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{ $ucfirst($t('expertTool.confirmation.verifyPrescription')) }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn @click="confirmCreateOrUpdatePrescription" x-large text class="ml-4" color="primary">
            {{ $ucfirst($t('confirm')) }}
          </v-btn>
          <v-btn @click="showConfirmationDialog = false" x-large text class="ml-4">
            {{ $ucfirst($t('general.close', { catalog:  'app_mobile' })) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- Create new or Update prescription popup -->

    <v-dialog v-model="showPrescriptionStatusDialog" width="600px">
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{ $ucfirst($t('expertTool.confirmation.isNew')) }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn @click="() => save(true)" x-large text class="ml-4 mt-2" color="primary">
            {{ $ucfirst($t('createNew')) }}
          </v-btn>
          <v-btn @click="() => save(false)" x-large text class="ml-4 mt-2">
            {{ $ucfirst($t('updateExisting')) }}
          </v-btn>
          <v-btn @click="showPrescriptionStatusDialog = false" x-large text class="ml-4 mt-2">
            {{ $ucfirst($t('buttons.cancel', {catalog: 'app_mobile'})) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';

/**
 * Component ExpertToolSave.vue
 *
 * @module ExpertToolSave
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/ExpertToolSave.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'ExpertToolSaveAndClose',
  data() {
    return {
      showUnableToSaveDialog: false,
      showConfirmationDialog: false,
      showPrescriptionStatusDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      isPrescriptionValid: 'expertTool/isPrescriptionValid',
      isRecommendationSaved: 'expertTool/isRecommendationSaved',
      hasPreviousRecommendation: 'expertTool/hasPreviousRecommendation',
      hasReadOnlyAccess: 'user/hasReadOnlyAccess'
    }),
    activeLevelOfGuidance() {
      const behaviourComponent = this.$store.state.patientSummary.behaviourComponents
        .find(item => item.key === 'STARTMOVING');
      return behaviourComponent?.levelOfGuidance;
    }
  },
  methods: {
    initSave() {
      this.$emit('click');
      if (!this.isPrescriptionValid) {
        this.showUnableToSaveDialog = true;
      } else if (this.isRecommendationSaved) {
        this.save();
      } else {
        this.showConfirmationDialog = true;
      }
    },
    confirmCreateOrUpdatePrescription() {
      if (this.hasPreviousRecommendation) {
        this.showConfirmationDialog = false;
        this.showPrescriptionStatusDialog = true;
      } else {
        this.save(true);
      }
    },
    async save(createNew) {
      this.showConfirmationDialog = false;
      this.showPrescriptionStatusDialog = false;

      if (this.isRecommendationSaved) {
        this.$emit('saved');
        return;
      }

      const success = await this.$store.dispatch(
          'expertTool/createPrescription', {
            isNew: createNew,
            startRecommendationNextWeek: this.$store.getters['expertTool/startRecommendationNextWeek']
          }
      );

      if (success) {
        this.$toasted.clear();
        this.$toasted
          .show(this.$ucfirst(this.$t('successMessages.exercisePrescriptionSaved')), {
            type : 'success',
            icon : 'check',
            position: 'bottom-center',
          });
        this.showNotification();
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', false);
        this.$emit('saved');
      }
    },
    showNotification() {
      // show notification if patient is not currently in LOG 2
      if (this.activeLevelOfGuidance === 2) {
        return;
      }
      this.$toasted.show(
        this.$ucfirst(
          this.$t("pa.goalSettingNoChangesIfLOGNotTwo", {
            params: {
              levelOfGuidance: '2',
            },
          })
        ), { icon: 'error', duration: 10000 }
      )
      return true;
    },
    closeUnableToSaveDialog() {
      this.showUnableToSaveDialog = false;
      this.$app.eventBus.$emit('afterCloseExperToolUnableToSaveDialog');
    },
  },
};
</script>
